/**
 * Post-verification onboarding — Sport / Main goal / Preferred therapist.
 *
 * Reached after the patient verifies their email and signs in for the first
 * time. Skippable — every field is optional. Once submitted, the server
 * auto-assigns the least-busy clinician (or the picked one) and marks the
 * caseload as "new".
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import { toast } from "sonner";
import { ArrowRight, Loader2, SkipForward } from "lucide-react";

export default function Onboarding() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sport, setSport] = useState("");
  const [goal, setGoal] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  // Skip straight to the dashboard if this isn't a patient or they've already
  // been onboarded — no dead-end for anyone who lands here by accident.
  useEffect(() => {
    if (!user) return;
    if (user.role !== "patient") { nav("/"); return; }
    if (user.onboarded_at) { nav("/app"); }
  }, [user, nav]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/auth/onboarding", {
        sport: sport.trim() || null,
        main_goal: goal.trim() || null,
        reason_for_appointment: reason.trim() || null,
      });
      toast.success("You're all set — welcome to Functional LAB");
      nav("/app");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save — try again");
    } finally { setSaving(false); }
  };

  const skip = () => nav("/app");

  return (
    <div className="min-h-screen fl-noise fl-page flex items-center justify-center p-6" style={{ background: "var(--fl-bg)" }}>
      <div className="absolute top-6 right-6 flex items-center gap-2"><LanguageSwitcher /><ThemeToggle /></div>
      <div className="w-full max-w-lg">
        <div className="mb-8 flex justify-center"><Logo /></div>
        <div className="fl-card p-8 space-y-5" data-testid="onboarding-card">
          <div>
            <div className="fl-label text-[#f47a20]">Welcome</div>
            <h1 className="fl-heading text-3xl mt-2">Tell us a little about yourself</h1>
            <p className="text-sm text-zinc-400 mt-2">A few quick optional questions so your clinician arrives at the first appointment already caught up.</p>
          </div>

          <div>
            <div className="fl-label mb-2">Sport / activity <span className="text-zinc-500 normal-case font-normal">· optional</span></div>
            <input className="fl-input" placeholder="Running, cycling, football..."
                   value={sport} onChange={(e) => setSport(e.target.value)}
                   data-testid="onboarding-sport" />
          </div>

          <div>
            <div className="fl-label mb-2">Main goal <span className="text-zinc-500 normal-case font-normal">· optional</span></div>
            <input className="fl-input" placeholder="Return to a 10K, back to lifting, marathon PB..."
                   value={goal} onChange={(e) => setGoal(e.target.value)}
                   data-testid="onboarding-goal" />
          </div>

          <div>
            <div className="fl-label mb-2">Reason for appointment <span className="text-zinc-500 normal-case font-normal">· optional</span></div>
            <textarea
              className="fl-input min-h-[92px] resize-y"
              placeholder="Knee pain since a 10K last month, dull ache when running downhill..."
              value={reason}
              maxLength={1000}
              onChange={(e) => setReason(e.target.value)}
              data-testid="onboarding-reason"
            />
            <div className="text-[11px] text-zinc-500 mt-1">Only your clinician sees this — it helps them prepare for your first session.</div>
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={skip} className="fl-btn-ghost text-sm flex-1 justify-center" data-testid="onboarding-skip">
              <SkipForward size={13} /> Skip for now
            </button>
            <button onClick={submit} disabled={saving}
                    className="fl-btn text-sm flex-1 justify-center" data-testid="onboarding-submit">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <ArrowRight size={13} />}
              {saving ? "Saving…" : "Continue"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
